import React, {useMemo, useState} from 'react';
import SettingsLayout from './SettingsLayout';
import {
  TeamesDropdown as Dropdown,
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
  PrimaryButton,
  SearchInput,
  TableLayout,
} from 'components';
import PageNumber from 'components/PageNumber';
import {Menu} from '@headlessui/react';
import {trashRed} from 'assets/images/images';
import AddEmailAddressModal from 'components/AddEmailAddressModal';
import {
  fetchNotificationEmails,
  deleteNotificationEmail,
} from 'requests/queries/settings';
import {useQuery, useMutation, useQueryClient} from 'react-query';
import {toast} from 'react-toastify';
import moment from 'moment';

function EmailNotifications() {
  const [addEmailModalOpen, setAddEmailModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [deletingEmailId, setDeletingEmailId] = useState(null);

  const queryClient = useQueryClient();

  const {
    data: emailData,
    isLoading,
    error,
  } = useQuery(
    [
      'notificationEmails',
      {page: currentPage, limit, filter: searchValue, environment: 'sandbox'},
    ],
    () =>
      fetchNotificationEmails({
        page: currentPage,
        limit,
        filter: searchValue,
      }),
    {
      onSuccess: data => {
        if (data.message) {
          toast.success(data.message);
        }
      },
      onError: error => {
        toast.error(error.response?.data?.error || 'Failed to fetch emails');
      },
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  const deleteEmailMutation = useMutation(
    ({roleId, data}) => deleteNotificationEmail(roleId, data),
    {
      onSuccess: () => {
        toast.success('Notification email deleted');
        queryClient.invalidateQueries('notificationEmails');
        setDeletingEmailId(null);
      },
      onError: error => {
        toast.error(
          error.response?.data?.error || 'Failed to delete email notification',
        );
        setDeletingEmailId(null);
      },
      onMutate: ({roleId}) => {
        setDeletingEmailId(roleId);
      },
    },
  );

  const notificationEmails = Array.isArray(emailData?.records)
    ? emailData.records
    : [];
  const totalRecords = emailData?.totalRecords || 0;
  const totalPages = emailData?.totalPages || 0;

  const actionMenuItems = useMemo(
    () => [
      {
        icon: trashRed,
        label: 'Delete',
        action: email => {
          deleteEmailMutation.mutate({
            roleId: email._id,
          });
        },
      },
    ],
    [deleteEmailMutation],
  );

  const pageClick = selected => {
    setCurrentPage(selected);
  };

  const isFiltered =
    notificationEmails.length === 0 && !isLoading && searchValue;

  return (
    <SettingsLayout breadCrumbs="Email Notifications">
      <p className="  text-sm text-[#5C688E] py-3">
        Add team members to email notifications to stay informed about balance
        alerts and key updates.
      </p>
      <div className="flex items-center justify-between min-h-[52px] py-2 flex-wrap gap-3 mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <SearchInput
          placeholder="Search email or name"
          onChange={value => {
            setSearchValue(value);
            setCurrentPage(1);
          }}
          defaultValue={searchValue}
        />
        <div className="flex items-center gap-4">
          <PrimaryButton
            buttonText="Add Email"
            onClick={() => setAddEmailModalOpen(true)}
          />
        </div>
      </div>

      {error ? (
        <div className="text-red-500 p-4">
          Error loading email notifications: {error.message}
        </div>
      ) : isLoading ? (
        <Loader height={50} />
      ) : isFiltered ? (
        <EmptyState
          height={55}
          body="This search did not return any data, try using different values."
          noBtn
        />
      ) : notificationEmails.length === 0 ? (
        <EmptyState
          height={55}
          body="No email address added yet"
          noBtn
          customBtn={
            <PrimaryButton
              buttonText="Add Email"
              className="mt-2 w-fit"
              onClick={() => setAddEmailModalOpen(true)}
            />
          }
        />
      ) : (
        <>
          <TableLayout negativeMargins>
            <thead className="font-semibold uppercase">
              <tr className="bg-white80">
                <th className="h-11 pl-4 text-left text-small font-medium text-[#5C688E] whitespace-nowrap sm:pl-6 xl:pl-8">
                  Name of Team Member
                </th>
                <th className="h-11 pl-0 text-left text-small font-medium text-[#5C688E] whitespace-nowrap">
                  Email Address
                </th>
                <th className="h-11 pl-0 text-left text-small font-medium text-[#5C688E] whitespace-nowrap">
                  Role
                </th>
                <th className="h-11 pl-0 text-left text-small font-medium text-[#5C688E] whitespace-nowrap">
                  Date/Time Added
                </th>
                <th className="h-11 pl-0 text-left text-small font-medium text-[#5C688E] whitespace-nowrap">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="text-left text-tiny">
              {notificationEmails.map(email => (
                <tr
                  key={email._id}
                  className="border-[0.5px] border-[#E4E3EC] cursor-pointer"
                >
                  <td className="p-4 pl-4 capitalize text-body whitespace-nowrap sm:pl-6 xl:pl-8">
                    {email.user?.first_name && email.user?.last_name
                      ? `${email.user.first_name} ${email.user.last_name}`
                      : 'N/A'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {email.email || 'N/A'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {email.permissions?.role || 'N/A'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    {email.notificationsEnabledAt
                      ? moment(email.notificationsEnabledAt).format(
                          'Do, MMMM YYYY, h:mma',
                        )
                      : 'N/A'}
                  </td>
                  <td className="p-4 pl-0 text-body whitespace-nowrap">
                    <div className="flex justify-center">
                      {email.permissions?.role !== 'Owner' ? (
                        <Dropdown topPosition="!p-0" gap="gap-0">
                          {actionMenuItems.map(item => (
                            <Menu.Item key={item.label}>
                              {() => (
                                <button
                                  className="z-40 p-3 flex items-center gap-4 w-full font-medium text-grey-500 hover:bg-grey-50 text-tiny"
                                  onClick={() => item.action(email)}
                                  disabled={
                                    item.label === 'Delete' &&
                                    deletingEmailId === email._id
                                  }
                                >
                                  {item.label === 'Delete' &&
                                  deletingEmailId === email._id ? (
                                    <span className="animate-spin h-4 w-4 border-2 border-t-transparent border-grey-500 rounded-full" />
                                  ) : (
                                    <img src={item.icon} alt="" />
                                  )}
                                  {item.label}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Dropdown>
                      ) : (
                        ''
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableLayout>
          {notificationEmails.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
              <PageNumber
                currentPage={currentPage}
                limit={limit}
                totalCount={totalRecords}
              />
              <div className="flex items-center gap-5">
                <PageLimit
                  onLimitChange={setLimit}
                  total={totalRecords}
                  length={notificationEmails.length}
                  limit={limit}
                />
                <Pagination
                  total={totalPages}
                  current={currentPage}
                  onPageChange={activePage => pageClick(activePage)}
                />
              </div>
            </div>
          )}
        </>
      )}

      <AddEmailAddressModal
        open={addEmailModalOpen}
        setOpen={setAddEmailModalOpen}
      />
    </SettingsLayout>
  );
}

export default EmailNotifications;
